import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmtEUR, withVAT } from "@/lib/api";
import { useCart } from "@/lib/cart";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

export default function Checkout() {
    const { items, subtotal, vatAmount, totalWithVat, clear } = useCart();
    const [coupon, setCoupon] = useState({ code: "", applied: null, error: null, busy: false });
    const nav = useNavigate();
    const [zones, setZones] = useState([]);
    const [zoneId, setZoneId] = useState("");
    const [method, setMethod] = useState("multibanco");
    const [submitting, setSubmitting] = useState(false);
    const [form, setForm] = useState({
        name: "",
        email: "",
        phone: "",
        nif: "",
        address: "",
        city: "",
        postal_code: "",
        country: "PT",
        notes: "",
        is_company: false,
        company_name: "",
        mbway_phone: "",
    });

    useEffect(() => {
        api.get("/shipping-zones").then((r) => {
            setZones(r.data);
            if (r.data.length) setZoneId(r.data[0].id);
        });
    }, []);

    const zone = zones.find((z) => z.id === zoneId);
    const shippingCost = useMemo(() => {
        if (!zone) return 0;
        return zone.free_above && subtotal >= zone.free_above ? 0 : zone.rate;
    }, [zone, subtotal]);
    const vatRate = items[0]?.vat_rate ?? 23;
    const shippingVAT = shippingCost * (vatRate / 100);
    const subtotalWithVAT = subtotal + vatAmount;
    const total = subtotalWithVAT + shippingCost + shippingVAT;
    const totalNoVAT = subtotal + shippingCost;
    const totalVATAmount = vatAmount + shippingVAT;

    const upd = (k) => (e) =>
        setForm((s) => ({ ...s, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        if (items.length === 0) return toast.error("Carrinho vazio");
        if (!zoneId) return toast.error("Selecione uma zona de envio");
        setSubmitting(true);
        try {
            const payload = {
                items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
                customer: {
                    name: form.name,
                    email: form.email,
                    phone: form.phone,
                    nif: form.nif || null,
                    address: form.address,
                    city: form.city,
                    postal_code: form.postal_code,
                    country: form.country,
                    notes: form.notes || null,
                    is_company: form.is_company,
                    company_name: form.is_company ? form.company_name : null,
                },
                shipping_zone_id: zoneId,
                payment_method: method,
                mbway_phone: method === "mbway" ? form.mbway_phone : null,
                coupon_code: coupon.applied?.code || null,
            };
            const { data } = await api.post("/orders", payload);
            clear();
            nav(`/encomenda/${data.id}/sucesso`);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Erro ao finalizar encomenda");
        } finally {
            setSubmitting(false);
        }
    };

    if (items.length === 0) {
        return (
            <div className="container-edge py-24 text-center">
                <h1 className="font-display font-black text-3xl">Carrinho vazio</h1>
                <p className="text-ink/60 mt-2">Adicione produtos antes do checkout.</p>
            </div>
        );
    }

    return (
        <div className="container-edge py-12" data-testid="checkout-page">
            <div className="label-mono text-magenta">Finalizar</div>
            <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter mt-2 mb-10">
                Checkout
            </h1>

            <form onSubmit={submit} className="grid lg:grid-cols-[1fr_400px] gap-8">
                <div className="space-y-8">
                    {/* Customer */}
                    <section className="border-2 border-ink bg-white p-6">
                        <h2 className="font-display font-black text-xl mb-5">1 · Dados de contacto</h2>
                        <div className="grid sm:grid-cols-2 gap-4">
                            <Field label="Nome completo *" value={form.name} onChange={upd("name")} required testid="checkout-name" />
                            <Field label="Email *" type="email" value={form.email} onChange={upd("email")} required testid="checkout-email" />
                            <Field label="Telefone *" value={form.phone} onChange={upd("phone")} required testid="checkout-phone" />
                            <Field label="NIF (para fatura)" value={form.nif} onChange={upd("nif")} testid="checkout-nif" />
                        </div>
                        <label className="mt-4 flex items-center gap-2 cursor-pointer">
                            <input type="checkbox" checked={form.is_company} onChange={upd("is_company")} className="accent-magenta w-4 h-4" />
                            <span className="text-sm font-bold uppercase tracking-wider">Compra como empresa (B2B)</span>
                        </label>
                        {form.is_company && (
                            <div className="mt-3">
                                <Field label="Nome da empresa" value={form.company_name} onChange={upd("company_name")} />
                            </div>
                        )}
                    </section>

                    {/* Shipping */}
                    <section className="border-2 border-ink bg-white p-6">
                        <h2 className="font-display font-black text-xl mb-5">2 · Morada de envio</h2>
                        <div className="grid sm:grid-cols-2 gap-4">
                            <div className="sm:col-span-2">
                                <Field label="Morada *" value={form.address} onChange={upd("address")} required testid="checkout-address" />
                            </div>
                            <Field label="Cidade *" value={form.city} onChange={upd("city")} required testid="checkout-city" />
                            <Field label="Código Postal *" value={form.postal_code} onChange={upd("postal_code")} required testid="checkout-postal" />
                        </div>

                        <div className="mt-5">
                            <div className="label-mono mb-3">Zona de envio · Transportadora</div>
                            <div className="space-y-2">
                                {zones.map((z) => (
                                    <label
                                        key={z.id}
                                        className={`flex items-center justify-between border-2 p-3 cursor-pointer ${
                                            zoneId === z.id ? "border-magenta bg-magenta/5" : "border-ink"
                                        }`}
                                        data-testid={`zone-${z.id}`}
                                    >
                                        <div className="flex items-center gap-3">
                                            <input
                                                type="radio"
                                                checked={zoneId === z.id}
                                                onChange={() => setZoneId(z.id)}
                                                className="accent-magenta"
                                            />
                                            <div>
                                                <div className="font-bold">{z.name}</div>
                                                <div className="text-xs font-mono text-ink/60">
                                                    {z.carrier} · {z.estimated_days} dias úteis
                                                </div>
                                            </div>
                                        </div>
                                        <div className="font-display font-black">
                                            {z.free_above && subtotal >= z.free_above ? "GRÁTIS" : fmtEUR(z.rate)}
                                        </div>
                                    </label>
                                ))}
                            </div>
                        </div>
                    </section>

                    {/* Payment */}
                    <section className="border-2 border-ink bg-white p-6">
                        <h2 className="font-display font-black text-xl mb-5">3 · Pagamento</h2>
                        <div className="space-y-2">
                            {[
                                { id: "multibanco", t: "Multibanco", s: "Referência MB · pagamento via homebanking ou ATM" },
                                { id: "mbway", t: "MB Way", s: "Receba notificação no telemóvel e pague em segundos" },
                                { id: "ccard", t: "Cartão de Crédito / Débito", s: "Visa, Mastercard via ifthenpay" },
                            ].map((m) => (
                                <label
                                    key={m.id}
                                    className={`block border-2 p-4 cursor-pointer ${
                                        method === m.id ? "border-magenta bg-magenta/5" : "border-ink"
                                    }`}
                                    data-testid={`payment-${m.id}`}
                                >
                                    <div className="flex items-center gap-3">
                                        <input
                                            type="radio"
                                            checked={method === m.id}
                                            onChange={() => setMethod(m.id)}
                                            className="accent-magenta"
                                        />
                                        <div className="flex-1">
                                            <div className="font-bold">{m.t}</div>
                                            <div className="text-xs font-mono text-ink/60">{m.s}</div>
                                        </div>
                                    </div>
                                    {m.id === "mbway" && method === "mbway" && (
                                        <div className="mt-3 ml-7">
                                            <Field
                                                label="Telefone MB Way *"
                                                value={form.mbway_phone}
                                                onChange={upd("mbway_phone")}
                                                placeholder="9XXXXXXXX"
                                                testid="mbway-phone"
                                            />
                                        </div>
                                    )}
                                </label>
                            ))}
                        </div>
                        <div className="mt-3 text-xs font-mono text-ink/60">
                            🔒 Modo SANDBOX activo — referências e pagamentos são simulados.
                            A fatura é gerada via Moloni após confirmação.
                        </div>
                    </section>

                    <section className="border-2 border-ink bg-white p-6">
                        <h2 className="font-display font-black text-xl mb-5">4 · Notas (opcional)</h2>
                        <textarea
                            value={form.notes}
                            onChange={upd("notes")}
                            rows={3}
                            className="input-edge"
                            placeholder="Informação adicional para a entrega…"
                            data-testid="checkout-notes"
                        />
                    </section>
                </div>

                {/* Summary */}
                <aside className="border-2 border-ink bg-white p-6 h-fit lg:sticky lg:top-32">
                    <h2 className="font-display font-black text-xl mb-5 border-b-2 border-ink pb-3">
                        Resumo da Encomenda
                    </h2>
                    <div className="space-y-2 max-h-72 overflow-y-auto pr-2">
                        {items.map((i) => (
                            <div key={i.product_id} className="flex justify-between gap-2 text-sm">
                                <div className="flex-1">
                                    <div className="font-bold line-clamp-1">{i.name}</div>
                                    <div className="text-xs font-mono text-ink/60">
                                        {i.quantity} × {fmtEUR(withVAT(i.price, i.vat_rate ?? 23))} c/IVA
                                    </div>
                                </div>
                                <div className="font-mono">{fmtEUR(withVAT(i.quantity * i.price, i.vat_rate ?? 23))}</div>
                            </div>
                        ))}
                    </div>
                    <div className="mt-5 pt-4 border-t-2 border-ink space-y-2 font-mono text-sm">
                        <div className="flex justify-between">
                            <span>Subtotal s/IVA</span>
                            <span>{fmtEUR(subtotal)}</span>
                        </div>
                        {coupon.applied && (
                            <div className="flex justify-between text-magenta" data-testid="coupon-applied">
                                <span>Cupão {coupon.applied.code}</span>
                                <span>-{fmtEUR(coupon.applied.discount_amount)}</span>
                            </div>
                        )}
                        <div className="flex justify-between">
                            <span>Portes</span>
                            <span>{shippingCost === 0 ? "GRÁTIS" : fmtEUR(shippingCost)}</span>
                        </div>
                        <div className="flex justify-between text-ink/60">
                            <span>IVA ({vatRate}%)</span>
                            <span>{fmtEUR(totalVATAmount)}</span>
                        </div>
                    </div>

                    {/* Coupon input */}
                    <div className="mt-4 pt-4 border-t border-ink/10">
                        {coupon.applied ? (
                            <div className="flex items-center justify-between bg-magenta/5 border border-magenta p-3">
                                <div className="text-sm">
                                    <div className="font-bold text-magenta">{coupon.applied.code}</div>
                                    <div className="text-xs font-mono text-ink/60">Desconto aplicado</div>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setCoupon({ code: "", applied: null, error: null, busy: false })}
                                    className="text-xs underline text-ink/60"
                                    data-testid="remove-coupon-btn"
                                >Remover</button>
                            </div>
                        ) : (
                            <div className="flex gap-2">
                                <input
                                    type="text"
                                    value={coupon.code}
                                    onChange={(e) => setCoupon({ ...coupon, code: e.target.value.toUpperCase(), error: null })}
                                    placeholder="CÓDIGO PROMOCIONAL"
                                    className="input-edge text-sm font-mono"
                                    data-testid="coupon-input"
                                />
                                <button
                                    type="button"
                                    onClick={async () => {
                                        if (!coupon.code) return;
                                        setCoupon({ ...coupon, busy: true });
                                        try {
                                            const { data } = await api.post("/coupons/validate", { code: coupon.code, subtotal });
                                            if (data.ok) {
                                                setCoupon({ code: data.coupon.code, applied: { ...data.coupon, discount_amount: data.discount_amount }, error: null, busy: false });
                                                toast.success(`Cupão aplicado: -${fmtEUR(data.discount_amount)}`);
                                            } else {
                                                setCoupon({ ...coupon, error: data.reason, busy: false });
                                                toast.error(data.reason);
                                            }
                                        } catch {
                                            setCoupon({ ...coupon, busy: false });
                                            toast.error("Erro a validar cupão");
                                        }
                                    }}
                                    disabled={!coupon.code || coupon.busy}
                                    className="btn-secondary text-xs px-3 py-2 disabled:opacity-40"
                                    data-testid="apply-coupon-btn"
                                >Aplicar</button>
                            </div>
                        )}
                    </div>

                    <div className="mt-4 pt-4 border-t-2 border-ink flex justify-between items-end">
                        <span className="label-mono">Total c/IVA</span>
                        <span className="font-display font-black text-2xl text-magenta" data-testid="checkout-total">
                            {fmtEUR(total - (coupon.applied?.discount_amount || 0) - (coupon.applied ? (coupon.applied.discount_amount * vatRate / 100) : 0))}
                        </span>
                    </div>
                    <button
                        type="submit"
                        disabled={submitting}
                        className="btn-primary w-full mt-6 disabled:opacity-50"
                        data-testid="confirm-order-btn"
                    >
                        {submitting && <Loader2 className="animate-spin" size={16} />}
                        {submitting ? "A processar…" : "Confirmar Encomenda"}
                    </button>
                </aside>
            </form>
        </div>
    );
}

function Field({ label, value, onChange, type = "text", required, placeholder, testid }) {
    return (
        <label className="block">
            <span className="label-mono mb-2 block">{label}</span>
            <input
                type={type}
                value={value}
                onChange={onChange}
                required={required}
                placeholder={placeholder}
                className="input-edge"
                data-testid={testid}
            />
        </label>
    );
}
